import { Button, useErrorState, useTimer, ValidatedInput } from '@finbeat/react-toolkit';
import { BOValidatedDatePicker } from 'base-components/BOValidatedDatePicker/BOValidatedDatePicker';
import { repeatPayment } from 'axios/paymentsToClientsApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { t } from 'i18next';
import { defaultFormatter } from 'mappers/errorFormatters';
import { PaymentToClientInfo } from 'models/api/paymentsToClients';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { Col, Container, Row } from 'reactstrap';
import i18n from 'utils/i18n';
import { AxiosError } from 'axios';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';

const formatter = (err?: unknown) => {
    const error = err as AxiosError<AxiosErrorResponse>;

    const errorData = error?.response?.data;

    if (errorData) {
        return (
            <>
                <h5>
                    <Trans i18nKey={errorData.errorCode} />
                </h5>

                {errorData.errorData && (
                    <ul>
                        <li>
                            <Trans i18nKey={errorData.errorData} />
                        </li>
                    </ul>
                )}
            </>
        );
    }

    return defaultFormatter(error);
};

const PaymentTab = ({ toggle }: { toggle: () => void }) => {
    const api = useApi();
    const { hasRole } = useAuth();
    const queryClient = useQueryClient();
    const { settings } = useGeneralSettingsContext();
    const { handleRequestError } = useErrorState();
    const { watch } = useFormContext<PaymentToClientInfo>();
    const [isTimeout, setTimeout] = useState(true);

    const paymentStatus = watch('paymentStatus');
    const elapsedTime = watch('elapsedTime') ?? 0;
    const orderId = watch('orderId');

    const { mutate } = useMutation('repeatPaymentMutation', () => repeatPayment(api, orderId), {
        onSuccess: () => {
            queryClient.invalidateQueries([['PaymentsToClients']]);
            toggle();
        },
        onError: (e) => {
            handleRequestError(e, 'BackOfficeOopsSomethingWentWrong', formatter);
        },
    });

    const repeatPaymentTimeout = useMemo(() => {
        const res = settings
            .find((x) => x.code === 'RepeatPaymentTimeout')
            ?.translations.find((x) => x.cultureCode === i18n.language)?.value;
        return res ? parseInt(res) : 0;
    }, [settings]);

    const { stop, play, reset } = useTimer({
        initialTime: elapsedTime,
        period: { sec: repeatPaymentTimeout },
        callback: () => {
            setTimeout(false);
        },
    });

    useEffect(() => {
        stop();
        reset({
            initialTime: elapsedTime,
            period: { sec: repeatPaymentTimeout },
            callback: () => {
                setTimeout(false);
            },
        });
        play();
    }, [repeatPaymentTimeout, elapsedTime]);

    const disabled = !(hasRole('paymentsToClientsRepeatPayment') && paymentStatus === 'Error');

    return (
        <Container>
            <Row>
                <Col>
                    <ValidatedInput<PaymentToClientInfo>
                        name="paymentId"
                        label={t('BackOfficeId')}
                        disabled
                    />
                </Col>
                <Col>
                    <ValidatedInput<PaymentToClientInfo>
                        name="paymentAmount"
                        label={t('BackOfficeAmount')}
                        disabled
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <BOValidatedDatePicker<PaymentToClientInfo>
                        name="paymentDt"
                        inputProps={{
                            label: t('BackOfficeDate'),
                        }}
                        disabled
                    />
                </Col>
                <Col>
                    <ValidatedInput<PaymentToClientInfo>
                        name="paymentAccount"
                        label={t('BackOfficeAccount')}
                        disabled
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput<PaymentToClientInfo>
                        name="msg"
                        type="textarea"
                        label={t('BackOfficePaymentSystemResponse')}
                        disabled
                    />
                </Col>
            </Row>
            <Row>
                <Col className="mr-auto">
                    <Button
                        color="success"
                        className="mr-auto"
                        disabled={disabled || isTimeout}
                        onClick={() => {
                            !disabled && mutate();
                        }}
                    >
                        <Trans i18nKey="BackOfficeRepeatPayment" />
                    </Button>
                </Col>
            </Row>
        </Container>
    );
};

export default PaymentTab;
